import { z } from 'zod'
import { LEAGUE_CODE_ENUM } from '@/lib/create/league-codes'

const userInstanceSchema = z
  .object({
    id: z.string().uuid(),
    user_id: z.string().uuid(),
    play_mode: z.enum(['cash', 'token']).nullable(),
    team_name: z.string().max(120).nullable(),
    nt_token_balance: z.number().finite().min(0),
    creation_completed: z.boolean(),
    creation_step: z.string().min(1).max(160),
    instance_status: z.literal('active'),
    season_id: z.string().uuid(),
    created_at: z.string().datetime({ offset: true }),
    updated_at: z.string().datetime({ offset: true }).optional(),
    league_id: z.string().uuid().nullable(),
    league_participant_id: z.string().uuid().nullable(),
    selected_leagues: z.array(LEAGUE_CODE_ENUM).optional(),
    league_count: z.number().int().min(0).optional(),
    lobby_assignment_status: z.string().max(80).nullable().optional(),
    squad_size: z.number().int().min(0).optional(),
    formation_name: z.string().max(40).nullable().optional(),
    has_captain: z.boolean().optional(),
    is_complete: z.boolean().optional()
  })
  .strict()

export const playerContextPresentationSchema = z
  .object({
    instances: z.array(userInstanceSchema).max(8),
    season_id: z.string().uuid()
  })
  .strict()

export type PlayerContextPresentation = z.infer<typeof playerContextPresentationSchema>
export type PresentedUserInstance = PlayerContextPresentation['instances'][number]

export async function fetchPlayerContext(): Promise<PlayerContextPresentation> {
  const response = await fetch('/api/player-context', {
    cache: 'no-store',
    credentials: 'same-origin',
    headers: { Accept: 'application/json' }
  })
  const payload = (await response.json().catch(() => null)) as {
    data?: unknown
    success?: unknown
  } | null
  const parsed = playerContextPresentationSchema.safeParse(payload?.data)
  if (!response.ok || payload?.success !== true || !parsed.success) {
    throw new Error('Player context is temporarily unavailable.')
  }
  return parsed.data
}
